/** @jsx isolateComponent */
import { Fragment } from 'react';

import { GridLayout } from '@zapier/common-components';
import {
  isolateComponent,
  labelStyles,
  responsiveStyles,
} from '@zapier/common-utils';
import { Colors, Icon, IconName, Spacer } from '@zapier/design-system';
import {
  BackgroundSection,
  CollapsedBlock,
  GlobalIncidents,
  HelloSection,
  RadioBlock,
  SizeType,
  UpgradeBlock,
} from '@zapier/product-get-help';
import { ContactFormAlertBanner } from 'app/get-help/components/ContactFormAlertBanner';
import { DeveloperContact } from 'app/get-help/components/DeveloperContact';
import { HelpCenterSuggestions } from 'app/get-help/components/HelpCenterSuggestions';
import { SalesContact } from 'app/get-help/components/SalesContact';
import { useFormContext } from 'app/get-help/contexts/FormContext';
import {
  CSS_CONTAINER_DEFAULT_PADDING,
  CSS_CONTAINER_MAX_WIDTH,
} from 'app/get-help/constants';

type Item = {
  icon: IconName;
  label: string;
  value: string;
};

type TopicProps = {
  actions: {
    answer: (any) => void;
    unanswer: () => void;
  };
  isCurrent: boolean;
  data: any;
  label: string;
  items: Item[];
  state: string;
};

const Styles = labelStyles(__filename, {
  collapsed: {
    display: 'grid',
    gridColumnGap: 13,
    gridTemplateColumns: 'min-content 1fr',
  },
  root: {
    backgroundColor: Colors.cream,
    flex: 1,
  },
  rootCollapsed: {
    backgroundColor: Colors.neutral100,
    flex: 1,
  },
  containerCollapsed: {
    maxWidth: CSS_CONTAINER_MAX_WIDTH,
    margin: '0px auto',
    padding: CSS_CONTAINER_DEFAULT_PADDING,
  },
  paidContainer: {
    margin: '70px auto -70px',
    padding: '40px 10px',
    maxWidth: CSS_CONTAINER_MAX_WIDTH,
  },
  container: (size: SizeType) =>
    responsiveStyles(size, {
      small: {
        margin: '70px auto',
        padding: '0px 10px',
      },
      large: {
        maxWidth: CSS_CONTAINER_MAX_WIDTH,
        padding: CSS_CONTAINER_DEFAULT_PADDING,
      },
    }),
});

const gapSize = 20;

const Topic = (props: TopicProps) => {
  const {
    actions: { answer, unanswer },
    isCurrent,
    label,
    items,
    data,
    state,
  } = props;

  const {
    size,
    user,
    isPaidEmailSupportEligible,
    showLoginComponent,
  } = useFormContext();
  const greeting =
    user && user.firstName
      ? `Hey ${user.firstName}, let's get you some help`
      : `Hey there, let's get you some help`;

  if (!isCurrent) {
    const selectedItem = items.find(item => item.value === data) as Item;

    return (
      <BackgroundSection
        containerPaddingBottom={40}
        containerPaddingTop={40}
        size={size}
        tint="light"
      >
        <HelloSection
          detailText="Get started by telling us a bit about your problem."
          title="Can’t find the help you need? Let’s dig deeper."
        />
        <GridLayout gap={gapSize}>
          <CollapsedBlock
            isDimmed={state !== 'confirm'}
            onClickEdit={unanswer}
            size={size}
            title={label}
          >
            <div css={Styles.collapsed}>
              <Fragment>
                <Icon color="neutral800" name={selectedItem.icon} size={20} />{' '}
                {selectedItem.label}
              </Fragment>
            </div>
          </CollapsedBlock>
        </GridLayout>
      </BackgroundSection>
    );
  }

  return displayUIByPlan({
    showLoginComponent,
    greeting,
    isPaidEmailSupportEligible,
    items,
    size,
    label,
    answer,
  });
};

function displayUIByPlan({
  showLoginComponent,
  greeting,
  isPaidEmailSupportEligible,
  items,
  size,
  label,
  answer,
}) {
  const contactFormContent = (
    <div>
      <RadioBlock
        items={items}
        onClick={({ value }) => answer(value)}
        size={size}
        title={label}
      />
    </div>
  );

  // These alerts should be displayed within a GridLayout.
  // 0 to all three of these alerts could be displayed at one time.
  const alertsSection = (
    <Fragment>
      <GlobalIncidents />
      <ContactFormAlertBanner />
    </Fragment>
  );

  const freePlanSection = (
    <Fragment>
      <BackgroundSection
        containerPaddingBottom={40}
        containerPaddingTop={40}
        size={size}
        tint="dark"
      >
        <HelloSection title={greeting} />
        <GridLayout gap={gapSize}>
          {alertsSection}
          <HelpCenterSuggestions size={size} />
          <Spacer height={20} />
          <UpgradeBlock />
        </GridLayout>
      </BackgroundSection>
      <BackgroundSection
        containerPaddingBottom={40}
        containerPaddingTop={40}
        size={size}
        tint="light"
      >
        <HelloSection
          detailText="Get started by telling us a bit about your problem."
          size={'small'}
          title="Can't find the help you need? Let's dig deeper"
        />
        <GridLayout gap={gapSize}>
          {contactFormContent}
          <SalesContact />
          <DeveloperContact />
        </GridLayout>
      </BackgroundSection>
    </Fragment>
  );

  const paidPlanSection = (
    <Fragment>
      <BackgroundSection
        containerPaddingBottom={40}
        containerPaddingTop={40}
        size={size}
        tint="light"
      >
        <HelloSection
          detailText="Get started by telling us a bit about your problem."
          title={greeting}
        />
        <GridLayout gap={gapSize}>
          {alertsSection}
          {contactFormContent}
          <DeveloperContact />
        </GridLayout>
      </BackgroundSection>
      <BackgroundSection
        containerPaddingBottom={40}
        containerPaddingTop={40}
        size={size}
        tint="dark"
      >
        <HelpCenterSuggestions size={size} />
        <Spacer height={20} />
      </BackgroundSection>
    </Fragment>
  );
  return (
    <Fragment>
      {!showLoginComponent && (
        <Fragment>
          {!isPaidEmailSupportEligible && freePlanSection}
          {isPaidEmailSupportEligible && paidPlanSection}
        </Fragment>
      )}
    </Fragment>
  );
}

export default Topic;
